import React, { useEffect, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useFetcher } from 'react-router-dom'

import Button from '@components/shared/Button'
import Dialog from '@components/shared/Dialog'
import LoadingSpinner from '@components/shared/LoadingSpinner'
import ScrollSelector from '@components/shared/form/ScrollSelector'
import { useAsyncEffect } from '@hooks/index'
import { useSelection } from '@providers/SelectionProvider'
import { ClientService } from '@services/index'
import { useDialogStore } from '@stores/dialog.store'
import { useSession } from '@stores/globalData/globalData.store'

import type Community from '@models/Community'
import type Score from '@models/Score'
import type { CopyToSiteResponse } from '@pages/home/actions/copyToSite.action'

const CopyToSiteDialog: React.FC = () => {
  const { t } = useTranslation()
  return (
    <Dialog name="copy_to_site" title={t('content.copyToSite')} maxWidth={415}>
      <CopyToSiteBody />
    </Dialog>
  )
}
export default CopyToSiteDialog

const CopyToSiteBody: React.FC = () => {
  const { t } = useTranslation()
  const session = useSession()

  const closeDialog = useDialogStore((s) => s.closeDialog)
  const { selectedItems } = useSelection<Score>()
  const fetcher = useFetcher<CopyToSiteResponse>()

  const [sites, setSites] = useState<Community[] | null>(null)
  const [selectedSite, setSelectedSite] = useState<Community | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  useAsyncEffect(async (isActive) => {
    const [resp] = await ClientService.fetchCommunities()
    if (resp && isActive()) {
      const { data } = resp
      setSites(data.communities)
    }
  }, [])

  // When the fetcher completes copying score(s) to site, handle response data
  useEffect(() => {
    if (!fetcher.data) return

    const { archive_notify } = fetcher.data
    if (archive_notify) {
      setSuccessMsg(t('content.scoresArchived'))
    } else {
      setSuccessMsg(t('content.scoresCopiedSuccessfully'))
    }
  }, [fetcher.data])

  if (successMsg) {
    return (
      <>
        <p className="mb-3">{successMsg}</p>
        <div className="flex flex-row w-full justify-end mt-4">
          <Button variant="secondary" type="button" onClick={() => closeDialog('copy_to_site')}>
            {t('action.close')}
          </Button>
        </div>
      </>
    )
  }

  return (
    <fetcher.Form action="/home/copyToSite" method="post">
      <p className="mb-3">{t('content.copyScoresToSite')}</p>
      {sites ? (
        <>
          {sites.length > 0 && (
            <ScrollSelector
              items={sites
                .filter(
                  (community) =>
                    community.active && community.hostname !== session.community.hostname
                )
                .map((community) => ({
                  text: community.name,
                  value: community.id,
                }))}
              onSelect={(id) => {
                setSelectedSite(sites?.find((site) => site.id === id) ?? null)
              }}
              className="mb-2"
            />
          )}
        </>
      ) : (
        <LoadingSpinner />
      )}

      {selectedSite && <input type="hidden" name="hostname" value={selectedSite.hostname} />}
      <input
        type="hidden"
        name="scoreIds"
        value={selectedItems.map((i) => i.external_id).join(',')}
      />

      <div className="flex flex-row w-full justify-end mt-4">
        <Button
          variant="primary"
          type="submit"
          disabled={!selectedSite || fetcher.state === 'submitting'}>
          {t('action.ok')}
        </Button>
      </div>
    </fetcher.Form>
  )
}
